// src/components/job-engine/useRecurring.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getRecurring } from "@/api/jobEngine";
import type { RecurringJob } from "@/api/jobEngine";

export interface UseRecurringResult {
  items: RecurringJob[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecurring(): UseRecurringResult {
  const [items, setItems] = useState<RecurringJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getRecurring(ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setItems(r ?? []); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load schedules");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [tick]);

  return { items, isLoading, error, refetch };
}
